import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { CloseOnBack } from "../../components/common/useCloseOnBack";
import { useTranslation } from "react-i18next";
import { supabase } from "../../supabase";
import { BACKEND_URL } from "../payments/vipps";
import toast from "react-hot-toast";
import "./KlippekortRedeem.css";
import "./Tickete.css"; // reuse the Tickets page's .t-header-row/.t-tabs so this page's top tab bar matches exactly
import { useNavigate } from "react-router-dom";
import { FiArchive, FiX, FiCalendar, FiHash, FiLayers, FiCheckCircle, FiUser, FiPlay, FiChevronRight, FiArrowLeft, FiInfo, FiCreditCard } from "react-icons/fi";
import { useRegisterBackHandler } from "../../components/common/BackHandlerContext";
import { FaGolfBall } from "react-icons/fa";
import BottomSheet from "../../components/common/BottomSheet";

// Same idea as Home/Orders/Settings: paint the last-known klippekort list instantly
// on a repeat visit instead of a blank screen while the same fetch re-runs.
const getCachedKlippekort = () => {
  try { return JSON.parse(localStorage.getItem('pws_klippekort_cache')); } catch { return null; }
};

export default function KlippekortRedeem({ onRedeem }) {
  const { t } = useTranslation('klippekortRedeem');
  const navigate = useNavigate();
  const cachedKK = getCachedKlippekort();
  const canUseCache = Boolean(cachedKK && localStorage.getItem('pws_auth') === '1');
  const [klippekortList, setKlippekortList] = useState(canUseCache ? cachedKK.klippekortList : []);
  const [user, setUser] = useState(null);
  const [allKlippekort, setAllKlippekort] = useState(canUseCache ? cachedKK.allKlippekort : []);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [localLoaded, setLocalLoaded] = useState(canUseCache);
  const [redeemingId, setRedeemingId] = useState(null);
  // activity type per card ('lek' or 'minigolf'), keyed by _uniqueId
  const [typeMap, setTypeMap] = useState({});
  // which klippekort's redeem panel is expanded, when there are multiple cards
  const [activeCardId, setActiveCardId] = useState(null);
  // detail modal (used for the active klippekort card)
  const [modalCard, setModalCard] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showInfoSheet, setShowInfoSheet] = useState(false);
  // Archive row detail view is a standalone page section, not a sheet - which card (if any) is open
  const [archiveDetailCard, setArchiveDetailCard] = useState(null);

  // On mobile the shared header's back arrow should step out of a local sub-view
  // (detail -> archive list -> main) instead of leaving the page entirely.
  useRegisterBackHandler(() => {
    if (archiveDetailCard) { setArchiveDetailCard(null); return; }
    if (showArchive) { setShowArchive(false); return; }
    navigate(-1);
  });

  const openCardModal = (card) => {
    setModalCard(card);
    setTimeout(() => setModalVisible(true), 20);
  };
  const closeCardModal = () => {
    setModalVisible(false);
    setTimeout(() => setModalCard(null), 280);
  };

  // Shared dots/stats/meta/history body - used by both the active-card modal and the
  // standalone archive detail page, so the two don't drift out of sync with each other.
  function renderKlippekortDetailBody(card) {
    const total = card.stampTotal || (card.stampAmounts + (card.stampUsed || 0));
    const used = total - (card.stampsLeft !== undefined ? card.stampsLeft : card.stampAmounts);
    const left = card.stampsLeft !== undefined ? card.stampsLeft : card.stampAmounts;
    const cardId = card.id && String(card.id).startsWith("KLK") ? card.id : (card.productId || card._uniqueId || "—");

    return (
      <>
        <span className="kr-detail-id-badge">
          <FiHash size={12} />
          {cardId}
        </span>

        <div className={total >= 20 ? 'kk-dots kk-dots--grid kr-modal-dots' : 'kk-dots kr-modal-dots'}>
          {Array.from({ length: total }, (_, i) => (
            <div key={i} className={`kk-dot${i < used ? ' used' : ' remaining'}`}>
              {i < used
                ? <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                : <svg viewBox="0 0 24 24" fill="none" stroke="#6B4FBB" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              }
            </div>
          ))}
        </div>

        <div className="kr-modal-stats">
          <div className="kr-modal-stat">
            <span className="kr-modal-stat-val">{total}</span>
            <span className="kr-modal-stat-lbl">{t('total')}</span>
          </div>
          <div className="kr-modal-stat">
            <span className="kr-modal-stat-val">{used}</span>
            <span className="kr-modal-stat-lbl">{t('used')}</span>
          </div>
          <div className="kr-modal-stat kr-modal-stat--yellow">
            <span className="kr-modal-stat-val">{left}</span>
            <span className="kr-modal-stat-lbl">{t('left')}</span>
          </div>
        </div>

        <div className="kr-modal-meta">
          {card.orderReference && (
            <div className="kr-modal-meta-row">
              <FiHash className="kr-modal-meta-icon" />
              <span>{t('order')}</span>
              <strong>{card.orderReference}</strong>
            </div>
          )}
          {card.datePurchased && (
            <div className="kr-modal-meta-row">
              <FiCalendar className="kr-modal-meta-icon" />
              <span>{t('purchased')}</span>
              <strong>{formatDate(card.datePurchased)}</strong>
            </div>
          )}
          {card.lastDayOfUse && (
            <div className="kr-modal-meta-row">
              <FiLayers className="kr-modal-meta-icon" />
              <span>{t('expires')}</span>
              <strong>{card.lastDayOfUse}</strong>
            </div>
          )}
        </div>

        {Array.isArray(card.usedDates) && card.usedDates.length > 0 && (
          <div className="kr-modal-history">
            <div className="kr-modal-history-label">{t('activationHistory')}</div>
            {[...card.usedDates].reverse().map((d, i) => (
              <div key={i} className="kr-modal-history-row">
                <FiCheckCircle className="kr-modal-history-icon" />
                <span>{formatDate(d)}</span>
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  const archiveList = useMemo(
    () => allKlippekort.filter(k => k.status === "deactivated" || k.stampsLeft === 0 || k.stampAmounts === 0),
    [allKlippekort]
  );

  const formatDate = (date) => {
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()} kl.${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  };


  React.useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const currentUser = session?.user || null;
      setUser(currentUser);
      if (!currentUser) {
        setAllKlippekort([]);
        setKlippekortList([]);
        setLocalLoaded(true);
        localStorage.removeItem('pws_klippekort_cache');
        navigate('/LogIn', { replace: true });
        return;
      }
      try {
        const { data: userRow } = await supabase.from('users').select('id').eq('auth_user_id', currentUser.id).maybeSingle();
        if (!userRow) { setLocalLoaded(true); return; }

        const [{ data: kkRows }, { data: orderRows }] = await Promise.all([
          supabase.from('klippekort').select('*').eq('user_id', userRow.id),
          supabase.from('orders').select('id, order_reference').eq('user_id', userRow.id),
        ]);
        const ordersById = new Map((orderRows || []).map(o => [o.id, o]));

        const kkIds = (kkRows || []).map(k => k.id);
        const { data: redemptionRows } = kkIds.length
          ? await supabase.from('tickets').select('klippekort_id, activated_at, date_purchased').in('klippekort_id', kkIds)
          : { data: [] };
        const redemptionsByKk = new Map();
        (redemptionRows || []).forEach(r => {
          const list = redemptionsByKk.get(r.klippekort_id) || [];
          list.push(r.activated_at || r.date_purchased);
          redemptionsByKk.set(r.klippekort_id, list);
        });

        const all = (kkRows || []).map(item => {
          const stampUsed = item.used_stamps || 0;
          const stampTotal = item.stamp_amounts || 0;
          const stampsLeft = stampTotal - stampUsed;
          return {
            id: item.id,
            name: item.name,
            duration: item.duration,
            bonusMinutes: item.bonus_minutes,
            productId: item.product_id,
            datePurchased: item.date_purchased,
            stampUsed,
            stampTotal,
            stampAmounts: stampsLeft,
            stampsLeft,
            status: stampsLeft <= 0 ? "deactivated" : "active",
            _uniqueId: item.id,
            orderId: item.order_id,
            orderReference: ordersById.get(item.order_id)?.order_reference,
            usedDates: redemptionsByKk.get(item.id) || [],
          };
        });
        const activeList = all.filter(k => k.status === "active" && k.stampsLeft > 0);
        setAllKlippekort(all);
        setKlippekortList(activeList);
        try {
          localStorage.setItem('pws_klippekort_cache', JSON.stringify({ allKlippekort: all, klippekortList: activeList }));
        } catch {}
      } catch (err) {
        console.error("Klippekort fetch error:", err);
        // Keep whatever's already shown (cache or a prior successful fetch) instead
        // of wiping the page to empty on a transient network error.
      }
      setLocalLoaded(true);
    });
    return () => subscription.unsubscribe();
  }, [reloadTrigger, navigate]);

  const handleRedeem = async (card, activityType = "lek") => {
    const qty = 1;
    const stampsLeft = card.stampsLeft !== undefined ? card.stampsLeft : card.stampAmounts;
    if (stampsLeft < qty || redeemingId === card._uniqueId) return;
    setRedeemingId(card._uniqueId);

    const nowDate = new Date().toISOString();
    const usedDates = Array.isArray(card.usedDates) ? [...card.usedDates, nowDate] : [nowDate];

    const isMinigolf = activityType === "minigolf";

    let bonusMinutes = 0;
    let rawBonus = null;
    if (typeof card.bonusMinutes === "number") rawBonus = card.bonusMinutes;
    else if (typeof card.bonusMinutes === "string") rawBonus = parseInt(card.bonusMinutes, 10);
    else if (typeof card.bonusTime === "number") rawBonus = card.bonusTime;
    else if (typeof card.bonusTime === "string") rawBonus = parseInt(card.bonusTime, 10);
    bonusMinutes = Number.isFinite(rawBonus) ? rawBonus : 10;

    // Only for the toast - the backend sets the ticket's real play time from the card.
    const duration = (Number(card.duration) > 0 ? Number(card.duration) : qty * 60) + bonusMinutes;
    const newStampsLeft = stampsLeft - qty;

    if (user && card.orderId) {
      try {
        // The backend counts the clip and creates its ticket in one go - the
        // app never writes those rows itself.
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${BACKEND_URL}/api/klippekort/redeem`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
          body: JSON.stringify({ klippekortId: card.id, activity: isMinigolf ? 'minigolf' : 'lek' }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.success) throw new Error(body.error || `HTTP ${res.status}`);
      } catch (err) {
        console.error("Redeem update failed:", err);
        setRedeemingId(null);
        toast.error(t('updateFailed'));
        return;
      }
    }

    if (newStampsLeft <= 0) {
      setKlippekortList(prev => prev.filter(k => k._uniqueId !== card._uniqueId));
      setTypeMap(prev => { const n = {...prev}; delete n[card._uniqueId]; return n; });
    } else {
      const updated = {
        ...card,
        stampUsed: (card.stampUsed || 0) + qty,
        stampsLeft: newStampsLeft,
        stampAmounts: newStampsLeft,
        usedDates,
      };
      setKlippekortList(prev => prev.map(k => k._uniqueId === card._uniqueId ? updated : k));
    }

    setRedeemingId(null);
    setReloadTrigger(prev => prev + 1);
    toast.success(
      isMinigolf
        ? t('minigolfRoundCreated')
        : t('ticketCreated', { duration }),
      { duration: 4000, position: "top-center" }
    );
  };

  if (!localLoaded) return null;

  if (!user) {
    return (
      <div className="kr-container">
        <div className="kr-empty-card">
          <p className="kr-empty-text">{t('loginToUseKlippekort')}</p>
          <button className="kr-action-btn" onClick={() => navigate("/login")}>{t('logIn')}</button>
          <button className="kr-action-btn kr-action-btn--yellow" onClick={() => navigate("/products")}>{t('buyKlippekort')}</button>
        </div>
      </div>
    );
  }

  const mainContent = (
    <div className="kr-container">
      <div className="t-header-row kr-top-tabs">
        <div className="t-tabs">
          {showArchive && (
            <button
              className="t-tab t-tab--icon kr-tab-back"
              onClick={() => (archiveDetailCard ? setArchiveDetailCard(null) : setShowArchive(false))}
              aria-label={archiveDetailCard ? t('backToArchive') : t('back')}
              title={archiveDetailCard ? t('backToArchive') : t('back')}
            >
              <FiArrowLeft size={16} />
            </button>
          )}
          <button className="t-tab" onClick={() => navigate("/tickets")}>{t('tickets')}</button>
          <button
            className={`t-tab t-tab--icon${showArchive ? " t-tab--active" : ""}`}
            onClick={() => { setShowArchive(v => !v); setArchiveDetailCard(null); }}
            aria-label={t('used')}
            title={t('used')}
          >
            <FiArchive size={16} />
          </button>
        </div>
        <button className="t-info-btn" onClick={() => setShowInfoSheet(true)} aria-label={t('howKlippekortWorks')} title={t('howKlippekortWorks')}>
          <FiInfo size={18} />
        </button>
      </div>
      {showArchive ? (
        archiveDetailCard ? (
          <>
            <div className="kk-section-label">{archiveDetailCard.name.replace(/\s*\(\d+\s*klipp( igjen)?\)/gi, '').trim()}</div>
            <div className="kr-archive-card kr-archive-detail">
              {renderKlippekortDetailBody(archiveDetailCard)}
            </div>
          </>
        ) : archiveList.length === 0 ? (
          <div className="kr-empty-card">
            <p className="kr-empty-text">{t('noUsedKlippekortYet')}</p>
          </div>
        ) : (
          <>
            <div className="kk-section-label">{t('archive')}</div>
            <div className="kr-archive-card">
              {archiveList.map((k, idx) => {
                const kTotal = k.stampTotal || (k.stampUsed || 0);
                const kId = k.id && String(k.id).startsWith("KLK") ? k.id : (k.productId || k._uniqueId || "—");
                return (
                  <div
                    key={k.id || k._uniqueId || idx}
                    className="kr-archive-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => setArchiveDetailCard(k)}
                    onKeyPress={e => (e.key === 'Enter' || e.key === ' ') && setArchiveDetailCard(k)}
                  >
                    <div className="kr-archive-row-left">
                      <div className="kr-archive-icon"><FiArchive /></div>
                      <div>
                        <h4>{k.name}</h4>
                        <p>{t('idLabel', { id: kId })}</p>
                      </div>
                    </div>
                    <div className="kr-archive-row-right">
                      <span className="kr-archive-used">{t('usedOfTotal', { used: k.stampUsed || 0, total: kTotal })}</span>
                      <FiChevronRight className="kr-archive-chevron" />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )
      ) : klippekortList.length === 0 ? (
        <div className="kr-empty-card">
          <p className="kr-empty-text">{t('noActiveKlippekort')}</p>
          <button className="kr-action-btn kr-action-btn--yellow" onClick={() => navigate("/products")}>{t('buyKlippekort')}</button>
        </div>
      ) : (() => {
        const activeCard = klippekortList.find(k => k._uniqueId === activeCardId) || klippekortList[0];
        const hasMultiple = klippekortList.length > 1;

        const card = activeCard;
        const total = card.stampTotal || (card.stampAmounts + (card.stampUsed || 0));
        const left = card.stampsLeft !== undefined ? card.stampsLeft : card.stampAmounts;
        const isRedeeming = redeemingId === card._uniqueId;
        const activityType = typeMap[card._uniqueId] || "lek";
        const isMinigolfSelected = activityType === "minigolf";

        const activeIndex = Math.max(0, klippekortList.findIndex(k => k._uniqueId === card._uniqueId));
        const baseName = card.name.replace(/\s*\(\d+\s*klipp( igjen)?\)/gi, "").trim();
        const goToIndex = (i) => {
          const clamped = Math.max(0, Math.min(klippekortList.length - 1, i));
          setActiveCardId(klippekortList[clamped]._uniqueId);
        };

        return (
          <>
            {/* Klippekort card: shows one at a time, swap via the pager below */}
            <div className="kk-section-label">{hasMultiple ? t('yourKlippekortWithCount', { current: activeIndex + 1, total: klippekortList.length }) : t('yourKlippekort')}</div>
            <div className="kk-card" role="button" tabIndex={0} onClick={() => openCardModal(card)} onKeyPress={e => (e.key === 'Enter' || e.key === ' ') && openCardModal(card)}>
              <div className="kk-card-top">
                <div>
                  <div className="kk-card-name">{baseName}</div>
                  <div className="kk-card-id">{t('idLabel', { id: card.id && String(card.id).startsWith("KLK") ? card.id : (card.productId || card._uniqueId || "—") })}</div>
                </div>
                <span className="kk-status-badge">{t('active')}</span>
              </div>
              <div className={total >= 20 ? "kk-dots kk-dots--grid" : "kk-dots"}>
                {Array.from({ length: total }, (_, i) => (
                  <div key={i} className={`kk-dot${i < (total - left) ? " used" : " remaining"}`}>
                    {i < (total - left)
                      ? <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      : <svg viewBox="0 0 24 24" fill="none" stroke="#6B4FBB" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    }
                  </div>
                ))}
              </div>
              <div className="kk-card-bottom">
                <div className="kk-remaining">
                  <strong>{left}</strong>
                  {t('punchesLeft')}
                </div>
                <div className="kk-expiry">
                  {card.lastDayOfUse ? t('expiresOn', { date: card.lastDayOfUse }) : t('noExpiryDate')}
                </div>
              </div>
            </div>

            {/* Swap between klippekort via prev/next arrows, instead of stacking all cards */}
            {hasMultiple && (
              <div className="kr-pager">
                <button
                  type="button"
                  className="kr-pager-arrow"
                  onClick={() => goToIndex(activeIndex - 1)}
                  disabled={activeIndex <= 0}
                >‹</button>
                <button
                  type="button"
                  className="kr-pager-arrow"
                  onClick={() => goToIndex(activeIndex + 1)}
                  disabled={activeIndex >= klippekortList.length - 1}
                >›</button>
              </div>
            )}

            {/* Redeem section — only for the selected klippekort */}
            <div className="kk-section-label">
              {hasMultiple ? t('redeemWithName', { name: card.name.replace(/\s*\(\d+\s*klipp( igjen)?\)/gi, "").trim() }) : t('redeem')}
            </div>
            <div className="kr-redeem-card">
              <div className="kr-redeem-title">{t('activity')}</div>
              <div className="kr-type-toggle">
                <button
                  type="button"
                  className={`kr-type-option${!isMinigolfSelected ? " active" : ""}`}
                  onClick={() => setTypeMap(prev => ({ ...prev, [card._uniqueId]: "lek" }))}
                >
                  <FiPlay className="kr-type-option-icon" />
                  <div className="kr-type-option-title">{t('play')}</div>
                </button>
                <button
                  type="button"
                  className={`kr-type-option${isMinigolfSelected ? " active" : ""}`}
                  onClick={() => setTypeMap(prev => ({ ...prev, [card._uniqueId]: "minigolf" }))}
                >
                  <FaGolfBall className="kr-type-option-icon" />
                  <div className="kr-type-option-title">{t('minigolf')}</div>
                </button>
              </div>

              <div className="kr-summary-box">
                <ul className="kr-summary-list">
                  <li>
                    <FiUser className="kr-summary-icon" />
                    <span className="kr-summary-person">{t('onePerson')}</span>
                    <span className="kr-summary-dot">·</span>
                    <span className="kr-summary-time">
                      {isMinigolfSelected ? t('minigolfOneRound') : t('playOneHour')}
                    </span>
                  </li>
                </ul>
              </div>

              <button
                className="kr-btn-redeem"
                onClick={() => handleRedeem(card, activityType)}
                disabled={left < 1 || isRedeeming}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                {isRedeeming ? t('redeeming') : t('redeemPunch')}
              </button>
            </div>
          </>
        );
      })()}
    </div>
  );

  const modal = modalCard && typeof document !== 'undefined'
    ? createPortal(
        <div
          className={`kr-modal-overlay${modalVisible ? ' visible' : ''}`}
          onClick={(e) => { if (e.target === e.currentTarget) closeCardModal(); }}
        >
          <CloseOnBack onClose={closeCardModal} />
          <div className={`kr-modal-sheet${modalVisible ? ' visible' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="kr-modal-handle" />

            <div className="kr-modal-header">
              <div className="kr-modal-title">{modalCard.name.replace(/\s*\(\d+\s*klipp( igjen)?\)/gi,'').trim()}</div>
              <button className="kr-modal-close" onClick={closeCardModal}><FiX size={16}/></button>
            </div>

            <div className="kr-modal-body">
              {renderKlippekortDetailBody(modalCard)}

              <button className="kr-modal-btn-ghost" onClick={closeCardModal}>{t('close')}</button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      {mainContent}
      {modal}
      {showInfoSheet && (
        <BottomSheet
          icon={<FiInfo size={20} />}
          iconColor="blue"
          title={t('howKlippekortWorks')}
          onClose={() => setShowInfoSheet(false)}
        >
          <div className="t-info-block">
            <div className="t-info-block-icon t-info-block-icon--purple"><FiCreditCard size={16} /></div>
            <div>
              <div className="t-info-block-title">{t('infoRedeemTitle')}</div>
              <div className="t-info-block-text">
                {t('infoRedeemText')}
              </div>
            </div>
          </div>
          <div className="t-info-block">
            <div className="t-info-block-icon"><FiCalendar size={16} /></div>
            <div>
              <div className="t-info-block-title">{t('infoNoExpiryTitle')}</div>
              <div className="t-info-block-text">
                {t('infoNoExpiryText')}
              </div>
            </div>
          </div>
          <div className="t-info-block">
            <div className="t-info-block-icon t-info-block-icon--orange"><FiArchive size={16} /></div>
            <div>
              <div className="t-info-block-title">{t('infoArchiveTitle')}</div>
              <div className="t-info-block-text">
                {t('infoArchiveText')}
              </div>
            </div>
          </div>
        </BottomSheet>
      )}
    </>
  );
}
